/**
 * Copyright 2025, IntraLinks, Inc. All rights reserved.
 */

import React, { ReactNode, useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import {
  IlIconInitializer, IlLoading, IlCard, IlButton, IlIcon, IlStates
} from 'il-framework-component';
import classnames from 'classnames';
import { RootState } from '../../app/store';
import Wrapper from '../../app/shared/components/Wrapper/Wrapper';
import { I18nNamespaces, FactorStatuses } from '../../app/shared/utils/constants.util';
import padlockIcon from '../../assets/padlock.svg';
import {
  loadSecuritySetupPage
} from './setupSecuritySlice';
import { ISupportedFactor, ISecurityFactor } from '../../domain/models/ISecuritySetup';
import styles from './SecuritySetup.scss';
import usePreserveSearchNav from '../../app/shared/hooks/usePreserveSearchNav';
import { capitalizeFirstLetter, maskEmailFixed, useRequiredLevel } from '../../app/shared/utils/helpers.util';
import { SecuritySetupType } from '../../domain/models/impl/SecuritySetup';
import { getFlowSnapshot } from '../../app/shared/utils/popup.util';
import { SecuritySetupRoutes } from '../../app/shared/utils/securitySetupRoutes';

function resolveSubtitle(t: (key: string, opts?: Record<string, string>) => string): ReactNode {
  const { resourceName } = getFlowSnapshot();
  if (resourceName) {
    return (
      <Trans
        i18nKey={t('loaEnrichment.chooseMethod.subtitleDynamic', { label: resourceName })}
        components={{ strong: <strong className={styles.promptLabel} /> }}
      />
    );
  }
  return t('loaEnrichment.chooseMethod.subtitleOne');
}

const VerifySection: React.FC<{ requiredLevel?: number }> = ({ requiredLevel }) => {
  const { t } = useTranslation(I18nNamespaces.LOA_ENRICHMENT);
  const enrolled = useSelector((s: RootState) => s.setupSecurity.enrolledFactors);
  const profileDetails = useSelector((s: RootState) => s.profile.profileDetails);
  const userEmail = profileDetails?.id ? profileDetails.externalId : '';
  const { pushPreserve, pushPreserveWithSearch } = usePreserveSearchNav();
  const { search } = useLocation();

  const TYPE_ORDER: Record<string, number> = {
    [SecuritySetupType.AUTHENTICATOR]: 0,
    [SecuritySetupType.SMS]: 1,
    [SecuritySetupType.EMAIL]: 2
  };

  const activeAtRequired = useMemo(
    () => (enrolled ?? []).filter(
      (f) => (f.status ?? '').toUpperCase() === FactorStatuses.ACTIVE
        && (typeof requiredLevel === 'number' ? f.level === requiredLevel : true)
        && (f.type === SecuritySetupType.AUTHENTICATOR || f.type === SecuritySetupType.SMS || f.type === SecuritySetupType.EMAIL)
    ).sort((first, second) => (TYPE_ORDER[first.type] ?? Infinity) - (TYPE_ORDER[second.type] ?? Infinity)),
    [enrolled, requiredLevel]
  );

  const go = (factor: ISecurityFactor) => {
    const params = new URLSearchParams(search || '');
    params.set('factorId', factor.id);

    switch (factor.type) {
      case SecuritySetupType.AUTHENTICATOR:
        pushPreserveWithSearch(SecuritySetupRoutes.verify('authenticator'), `?${params.toString()}`);
        break;
      case SecuritySetupType.SMS:
        pushPreserveWithSearch(SecuritySetupRoutes.verify('sms'), `?${params.toString()}`);
        break;
      case SecuritySetupType.EMAIL:
        pushPreserveWithSearch(SecuritySetupRoutes.verify('email'), `?${params.toString()}`);
        break;
      default:
        pushPreserve(SecuritySetupRoutes.root);
    }
  };

  const getCodeFactorText = (factor: ISecurityFactor) => {
    const factorType = factor.type;
    const phoneNumber = (factor?.options?.phoneNumber || '');
    switch (factorType) {
      case SecuritySetupType.AUTHENTICATOR:
        return t('loaEnrichment.common.getCode.authenticator');
      case SecuritySetupType.SMS:
        return t('loaEnrichment.common.getCode.sms', { phoneNumber });
      case SecuritySetupType.EMAIL:
        return t('loaEnrichment.common.getCode.email', { email: maskEmailFixed(userEmail) });
      default:
        return '';
    }
  };

  return (
    <>
      <h1 className={classnames(styles.formTitle, styles.title)}>
        {t('loaEnrichment.common.titles.verifyIdentity')}
      </h1>
      {activeAtRequired.length === 0 ? (
        <div className={styles.stateSectionContainer}>
          <IlStates
            illustration='no-results'
            title={t('loaEnrichment.empty.verify.title')}
            description={t('loaEnrichment.empty.verify.description', { level: requiredLevel })}
          />
        </div>
      ) : (
        <>
          <p className={styles.helpTextOne}>{t('loaEnrichment.common.titles.howToVerify')}</p>
          <p className={styles.helpTextTwo}>{resolveSubtitle(t)}</p>
          <ul className={styles.activeFactorsList}>
            {activeAtRequired.map((f) => (
              <li key={f.id}>
                <button className={styles.factorBtn} type='button' onClick={() => go(f)}>
                  <p className={styles.factorDescription}>
                    <span className={styles.labelText}>{capitalizeFirstLetter(f?.label as string)}</span>
                    {getCodeFactorText(f)}
                  </p>
                  <span className={styles.arrowIconWrapper}><IlIcon name='chevron-right' /></span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
};

const EnrollSection: React.FC<{ requiredLevel?: number }> = ({ requiredLevel }) => {
  const { t } = useTranslation(I18nNamespaces.LOA_ENRICHMENT);
  const supportedFactors = useSelector((s: RootState) => s.setupSecurity.supportedFactors);
  const actionLoading = useSelector((s: RootState) => s.setupSecurity.actionLoading);
  const { pushPreserve } = usePreserveSearchNav();

  const selectFactor = (factor: ISupportedFactor) => {
    switch (factor.type) {
      case SecuritySetupType.AUTHENTICATOR:
        pushPreserve(SecuritySetupRoutes.enroll('authenticator'));
        break;
      case SecuritySetupType.SMS:
        pushPreserve(SecuritySetupRoutes.enroll('sms'));
        break;
      case SecuritySetupType.EMAIL:
        pushPreserve(SecuritySetupRoutes.enroll('email'));
        break;
      default:
        pushPreserve(SecuritySetupRoutes.root);
        break;
    }
  };

  const filteredSupported = useMemo(
    () => supportedFactors.filter((f) => (typeof requiredLevel === 'number' ? f.loaLevel === requiredLevel : true)),
    [supportedFactors, requiredLevel]
  );

  return (
    <>
      <h1 className={classnames(styles.formTitle, styles.title)}>
        {t('loaEnrichment.common.titles.accountSecuritySetup')}
      </h1>

      {filteredSupported.length === 0 ? (
        <div className={styles.stateSectionContainer}>
          <IlStates
            illustration='empty'
            title={t('loaEnrichment.empty.enroll.title')}
            description={t('loaEnrichment.empty.enroll.description', { level: requiredLevel })}
          />
        </div>
      ) : (
        <>
          <p className={styles.titleHelpText}>{t('loaEnrichment.chooseMethod.title')}</p>
          <p className={styles.subtitleHelpTextOne}>{resolveSubtitle(t)}</p>
          <p className={styles.subtitleHelpTextTwo}>{t('loaEnrichment.chooseMethod.subtitleTwo')}</p>
          <ul className={styles.factorsList}>
            {filteredSupported.map((factor) => (
              <li key={factor.id}>
                <span id={factor.id} className={styles.factorData}>
                  <span>
                    <span className={styles.factorTitle}>{t(factor.title)}</span>
                    <span className={styles.securityLevel}>
                      {t('loaEnrichment.common.labels.securityLevel')}: {' '}
                      {[...Array(factor.securityLevel)].map((_, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <img key={i} src={padlockIcon} alt={t('loaEnrichment.common.labels.securityLevel')} />
                      ))}
                    </span>
                  </span>

                  <span>
                    <IlButton
                      kind='secondary'
                      size='small'
                      onClick={() => selectFactor(factor)}
                      id={`set-${factor.id}`}
                      disabled={actionLoading}
                    >
                      {t('loaEnrichment.common.buttons.setUp')}
                    </IlButton>
                  </span>
                </span>

                {!!factor.helpText && (
                  <span className={styles.listHelp}>{t(factor.helpText)}</span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
};

const SecuritySetup: React.FC = () => {
  const { t } = useTranslation(I18nNamespaces.LOA_ENRICHMENT);
  const dispatch = useDispatch();
  const pageLoading = useSelector((s: RootState) => s.setupSecurity.pageLoading);
  const profileDetails = useSelector((s: RootState) => s.profile.profileDetails);
  const ssoEnabled = profileDetails?.id ? profileDetails.ssoEnabled : undefined;
  const factorsLoaded = useSelector((s: RootState) => s.setupSecurity.factorsLoaded);
  const requiredLevel = useRequiredLevel();
  const { pathname } = useLocation();
  const isEntryRoute = pathname === SecuritySetupRoutes.root || pathname === `${SecuritySetupRoutes.root}/`;

  const hasActiveAtRequired = useSelector((s: RootState) => (s.setupSecurity.enrolledFactors ?? []).some(
    (f) => (f.status ?? '').toUpperCase() === FactorStatuses.ACTIVE
      && (typeof requiredLevel === 'number' ? f.level === requiredLevel : true)
      && (f.type === SecuritySetupType.AUTHENTICATOR || f.type === SecuritySetupType.SMS || f.type === SecuritySetupType.EMAIL)
  ));

  useEffect(() => {
    if (ssoEnabled !== true) return;
    dispatch(loadSecuritySetupPage(t));
  }, [dispatch, ssoEnabled, t]);

  if (ssoEnabled === undefined) {
    return <IlLoading withOverlay />;
  }

  if (ssoEnabled === true && !factorsLoaded) return <IlLoading withOverlay />;

  if (ssoEnabled === false && isEntryRoute) {
    return null;
  }

  return (
    <>
      {pageLoading && <IlLoading withOverlay />}
      <IlIconInitializer />
      <Wrapper>
        <IlCard className={styles.cardForm}>
          {!pageLoading
            && (hasActiveAtRequired
              ? <VerifySection requiredLevel={requiredLevel} />
              : <EnrollSection requiredLevel={requiredLevel} />
            )}
        </IlCard>
      </Wrapper>
    </>
  );
};

export default SecuritySetup;
